import { useState } from 'react';
import { format } from 'date-fns';
import { T, useGT } from 'gt-react';
import { DownloadSimpleIcon, XIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import type {
  VendorPortalFile,
  VendorPortalFileType,
  VendorPortalVendor,
} from '@erp/rpc';
import Page from '@/components/page/Page';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  type DateTimeRange,
  DateTimeRangePicker,
} from '@/components/common/date-pickers/date-time-range-picker';
import { TablePagination } from '@/components/common/TablePagination';
import { useCan } from '@/hooks/useCan';
import { useDebouncedSearchInput } from '@/hooks/useDebouncedSearchInput';
import { formatFileSize } from '@/lib/file-upload-validation';
import {
  VENDOR_PORTAL_FILES_PAGE_SIZES,
  useVendorPortalFiles,
} from './useVendorPortalFiles';
import {
  type VendorPortalsSearch,
  downloadedRangeFromSearch,
  filtersFromSearch,
  pageFromSearch,
} from './vendorPortalsSearch';
import { useVendorPortalFileActions } from './useVendorPortalFileActions';
import { VendorPortalSelectionBar } from './VendorPortalSelectionBar';
import {
  VendorPortalDownloadAllButton,
  VendorPortalRunFilterChip,
  VendorPortalStartedDownloads,
} from './VendorPortalDownloadAll';
import { VendorPortalFailedRuns } from './VendorPortalFailedRuns';
import { VendorPortalFileSource } from './VendorPortalFileSource';
import { VendorPortalUploadButton } from './VendorPortalUploadDialog';

const ROW_DATETIME_FORMAT = 'MM/dd/yyyy HH:mm';
// Select values: vendors are prefixed so no workflow name can collide with "all".
const ALL_VENDORS = 'all';
const VENDOR_VALUE_PREFIX = 'vendor:';
const ALL_FILE_TYPES = 'all';
const FILE_TYPE_VALUE_PREFIX = 'type:';
const COLUMN_COUNT = 8;
const ACCOUNT_FILTER_DEBOUNCE_MS = 300;

/**
 * Files the organization's workflows downloaded from Vendor Portals with the Browser
 * Use Agent. The vendor is the workflow's name; the Vendor Account is the one the
 * downloading step was asked for. The filters and the page live in the URL (`search`).
 */
export function VendorPortalsPage({
  search,
  onSearchChange,
}: {
  search: VendorPortalsSearch;
  onSearchChange: (
    next: VendorPortalsSearch,
    options?: { replace?: boolean },
  ) => void;
}) {
  const gt = useGT();
  const [canDeletePricing] = useCan(Action.Delete, Subject.Pricing);
  const filters = filtersFromSearch(search);
  const { limit: pageSize, offset } = pageFromSearch(search);
  const downloadedRange = downloadedRangeFromSearch(search);
  const { data, isLoading, isPlaceholderData, error } = useVendorPortalFiles({
    ...filters,
    limit: pageSize,
    offset,
  });

  const { files, total, vendors, fileTypes } = listedPage(data);
  const actions = useVendorPortalFileActions(
    files,
    JSON.stringify([filters, pageSize, offset]),
  );

  /** A new filter starts again from the first page. */
  function changeFilters(
    patch: Partial<VendorPortalsSearch>,
    options?: { replace?: boolean },
  ) {
    onSearchChange({ ...search, ...patch, page: undefined }, options);
  }

  // Typing reaches the URL once the user pauses, replacing the entry so Back skips
  // each keystroke.
  const accountInput = useDebouncedSearchInput({
    committed: search.account,
    onCommit: (next) => changeFilters({ account: next }, { replace: true }),
    delayMs: ACCOUNT_FILTER_DEBOUNCE_MS,
  });

  function goToOffset(next: number) {
    const page = Math.floor(next / pageSize) + 1;
    onSearchChange({ ...search, page: page > 1 ? page : undefined });
  }

  function handlePageSizeChange(next: number) {
    changeFilters({ pageSize: next });
  }

  function handleVendorChange(next: string) {
    changeFilters({ vendor: vendorFromSelectValue(next) });
  }

  function handleFileTypeChange(next: string) {
    changeFilters({ fileType: fileTypeFromSelectValue(next) });
  }

  function handleDownloadedRangeChange(next: DateTimeRange | undefined) {
    changeFilters({
      downloadedFrom: next?.from?.toISOString(),
      downloadedTo: next?.from ? next.to?.toISOString() : undefined,
    });
  }

  function handleRunChange(next: string | undefined) {
    changeFilters({ run: next });
  }

  return (
    <Page
      title={gt('Vendor Portals', { context: 'Vendor portals page title' })}
      subtitle={gt('Files your workflows downloaded from vendor portals', {
        context: 'Vendor portals page subtitle',
      })}
      error={error ?? undefined}
      actions={
        <VendorPortalFailedRuns
          onDownloadSummary={(summary) => void actions.downloadFile(summary)}
        />
      }
    >
      <div className="space-y-2">
        <Card>
          <div className="flex items-center gap-2">
            <VendorFilter
              value={vendorSelectValue(search.vendor)}
              vendors={vendors}
              onChange={handleVendorChange}
            />
            <VendorAccountFilter
              value={accountInput.value}
              onChange={accountInput.onChange}
            />
            <FileTypeFilter
              value={fileTypeSelectValue(search.fileType)}
              fileTypes={fileTypes}
              onChange={handleFileTypeChange}
            />
            <DownloadedFilter
              value={downloadedRange}
              onChange={handleDownloadedRangeChange}
            />
            <VendorPortalRunFilterChip
              workflowRunId={search.run}
              onClear={() => handleRunChange(undefined)}
            />
            <VendorPortalDownloadAllButton
              total={total}
              pageFileCount={files.length}
              disabled={isPlaceholderData}
              onDownloadAll={() => void actions.downloadAll(filters)}
              onDownloadPage={() => void actions.downloadPage(offset)}
            />
            <VendorPortalUploadButton />
            <VendorPortalSelectionBar
              count={actions.selectedFiles.length}
              canDelete={canDeletePricing}
              isDeleting={actions.isDeleting}
              onDownload={() => void actions.downloadSelected()}
              onDelete={actions.deleteSelected}
              onClear={actions.clearSelection}
            />
          </div>
        </Card>

        <VendorPortalStartedDownloads downloads={actions.startedDownloads} />

        <Card className="overflow-auto">
          <Table>
            <TableHeader sticky>
              <TableRow>
                <TableHead className="w-[40px]">
                  <SelectAllFilesCheckbox
                    fileCount={files.length}
                    selectedCount={actions.selectedFiles.length}
                    disabled={isPlaceholderData}
                    onChange={actions.toggleAll}
                  />
                </TableHead>
                <TableHead>
                  <T context="Vendor portals table column header">File</T>
                </TableHead>
                <TableHead className="w-[200px]">
                  <T context="Vendor portals table column header">Vendor</T>
                </TableHead>
                <TableHead className="w-[240px]">
                  <T context="Vendor portals table column header">Account</T>
                </TableHead>
                <TableHead className="w-[150px]">
                  <T context="Vendor portals table column header">Downloaded</T>
                </TableHead>
                <TableHead className="w-[100px]">
                  <T context="Vendor portals table column header">Size</T>
                </TableHead>
                <TableHead className="w-[100px]">
                  <T context="Vendor portals table column header">Source</T>
                </TableHead>
                <TableHead className="w-[130px]">
                  <span className="sr-only">
                    <T context="Vendor portals table action column">Actions</T>
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <VendorPortalFileRows
                isLoading={isLoading}
                // The previous query's rows stay on screen while the next loads; they
                // can't be selected (and so deleted) until they belong to this query.
                selectable={!isPlaceholderData}
                files={files}
                selectedIds={actions.selectedIds}
                onToggleSelected={actions.toggle}
                onOnlyRun={handleRunChange}
                onDownload={(file) => void actions.downloadFile(file)}
              />
            </TableBody>
          </Table>
        </Card>

        <VendorPortalFilesPagination
          offset={offset}
          pageSize={pageSize}
          shownCount={files.length}
          total={total}
          onOffsetChange={goToOffset}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </Page>
  );
}

/**
 * Page controls and the rows-per-page choice. Every page size fits in one zip, so more
 * files than a zip takes are downloaded a page at a time. Also shown past the first page
 * so files that disappear from under a later page never strand the user there.
 */
function VendorPortalFilesPagination({
  offset,
  pageSize,
  shownCount,
  total,
  onOffsetChange,
  onPageSizeChange,
}: {
  offset: number;
  pageSize: number;
  shownCount: number;
  total: number;
  onOffsetChange: (next: number) => void;
  onPageSizeChange: (next: number) => void;
}) {
  // Nothing to page or resize while every file fits on the smallest page.
  if (total <= VENDOR_PORTAL_FILES_PAGE_SIZES[0] && offset === 0) return null;
  const lastShown = offset + shownCount;
  return (
    <TablePagination
      pagination={{
        pageSize,
        currentPage: Math.floor(offset / pageSize) + 1,
        hasNextPage: lastShown < total,
        hasPreviousPage: offset > 0,
        totalItems: total,
        startItem: total === 0 ? 0 : offset + 1,
        endItem: lastShown,
      }}
      rowsPerPageOptions={[...VENDOR_PORTAL_FILES_PAGE_SIZES]}
      onPageSizeChange={onPageSizeChange}
      onPreviousPage={() => onOffsetChange(Math.max(0, offset - pageSize))}
      onNextPage={() => onOffsetChange(offset + pageSize)}
    />
  );
}

function VendorFilter({
  value,
  vendors,
  onChange,
}: {
  value: string;
  vendors: VendorPortalVendor[];
  onChange: (next: string) => void;
}) {
  const gt = useGT();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-8 w-[200px] lg:w-[260px]"
        aria-label={gt('Filter by vendor', {
          context: 'Vendor portals vendor filter label',
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_VENDORS}>
          <T context="Vendor portals vendor filter option">All vendors</T>
        </SelectItem>
        {vendors.map((entry) => (
          <SelectItem
            key={entry.name}
            value={`${VENDOR_VALUE_PREFIX}${entry.name}`}
          >
            {entry.name} ({entry.fileCount})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FileTypeFilter({
  value,
  fileTypes,
  onChange,
}: {
  value: string;
  fileTypes: VendorPortalFileType[];
  onChange: (next: string) => void;
}) {
  const gt = useGT();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-8 w-[140px] lg:w-[160px]"
        aria-label={gt('Filter by file type', {
          context: 'Vendor portals file type filter label',
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_FILE_TYPES}>
          <T context="Vendor portals file type filter option">All file types</T>
        </SelectItem>
        {fileTypes.map((entry) => (
          <SelectItem
            key={entry.name}
            value={`${FILE_TYPE_VALUE_PREFIX}${entry.name}`}
          >
            .{entry.name} ({entry.fileCount})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The Vendor Account a file was downloaded for: its number, then its name. */
function VendorAccountLabel({
  account,
}: {
  account: VendorPortalFile['account'];
}) {
  if (!account) return <>—</>;
  return (
    <span>
      <span className="font-mono text-xs">{account.number}</span>
      {account.name && (
        <span className="ml-2 text-muted-foreground">{account.name}</span>
      )}
    </span>
  );
}

function VendorAccountFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const gt = useGT();
  return (
    <Input
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={gt('Account number or name', {
        context: 'Vendor portals Vendor Account filter placeholder',
      })}
      aria-label={gt('Filter by Vendor Account', {
        context: 'Vendor portals Vendor Account filter label',
      })}
      className="h-8 w-[200px] lg:w-[260px]"
    />
  );
}

/**
 * When files were downloaded: whole days, or to the second with "Include specific times"
 * for when the timestamp matters.
 *
 * The picker reads whether to show times only when it mounts, so a range that arrives
 * from outside it (Back, Forward, a link) remounts it; its own edits do not, or the
 * popover would close mid-edit.
 */
function DownloadedFilter({
  value,
  onChange,
}: {
  value: DateTimeRange | undefined;
  onChange: (next: DateTimeRange | undefined) => void;
}) {
  const gt = useGT();
  const rangeKey = downloadedRangeKey(value);
  // `ownRangeKey` is the range the picker just sent, until that change reaches the URL;
  // any other change came from outside. Used up either way, so a later Back or Forward
  // to the same range still counts as outside.
  const [picker, setPicker] = useState<{
    rangeKey: string;
    ownRangeKey: string | null;
    generation: number;
  }>({ rangeKey, ownRangeKey: null, generation: 0 });
  if (picker.rangeKey !== rangeKey) {
    setPicker({
      rangeKey,
      ownRangeKey: null,
      generation: picker.generation + (rangeKey === picker.ownRangeKey ? 0 : 1),
    });
  }

  function change(next: DateTimeRange | undefined) {
    setPicker((current) => ({
      ...current,
      ownRangeKey: downloadedRangeKey(next),
    }));
    onChange(next);
  }

  return (
    <span className="inline-flex items-center">
      <DateTimeRangePicker
        key={picker.generation}
        className="h-8 w-[220px] lg:w-[260px]"
        placeholder={gt('Downloaded', {
          context:
            'Vendor portals filter placeholder for the range of days files were downloaded',
        })}
        value={value}
        onChange={change}
        defaultIncludeTime={hasTimeOfDay(value)}
      />
      {value && (
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => change(undefined)}
          aria-label={gt('Clear the Downloaded filter', {
            context:
              'Vendor portals button that clears the range of days and times files were downloaded',
          })}
        >
          <XIcon className="h-3 w-3" />
        </Button>
      )}
    </span>
  );
}

/** Ticks or clears every file on the page. */
function SelectAllFilesCheckbox({
  fileCount,
  selectedCount,
  disabled,
  onChange,
}: {
  fileCount: number;
  selectedCount: number;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const gt = useGT();
  return (
    <Checkbox
      checked={selectAllState(fileCount, selectedCount)}
      disabled={disabled || fileCount === 0}
      onCheckedChange={(checked) => onChange(checked === true)}
      aria-label={gt('Select all files', {
        context: 'Vendor portals checkbox that selects every file on the page',
      })}
    />
  );
}

/** The header checkbox: all, none, or some of the page's files ticked. */
function selectAllState(
  fileCount: number,
  selectedCount: number,
): boolean | 'indeterminate' {
  if (selectedCount === 0) return false;
  return selectedCount === fileCount ? true : 'indeterminate';
}

/** The page's files, their total and the vendor and file type counts; empty until the list loads. */
function listedPage(data: ReturnType<typeof useVendorPortalFiles>['data']) {
  return {
    files: data?.items ?? [],
    total: data?.total ?? 0,
    vendors: data?.vendors ?? [],
    fileTypes: data?.fileTypes ?? [],
  };
}

/** The vendor filter's value for a vendor, or "all". */
function vendorSelectValue(vendor: string | undefined): string {
  return vendor === undefined ? ALL_VENDORS : `${VENDOR_VALUE_PREFIX}${vendor}`;
}

/** The file type filter's value for a file type, or "all". */
function fileTypeSelectValue(fileType: string | undefined): string {
  return fileType === undefined
    ? ALL_FILE_TYPES
    : `${FILE_TYPE_VALUE_PREFIX}${fileType}`;
}

/** One string per range, to tell one range from another. */
function downloadedRangeKey(range: DateTimeRange | undefined): string {
  return `${range?.from?.getTime() ?? ''}|${range?.to?.getTime() ?? ''}`;
}

/** Whether a range was picked to the time of day rather than as whole days. */
function hasTimeOfDay(range: DateTimeRange | undefined): boolean {
  const isMidnight = (date: Date) =>
    date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;
  const isLastSecond = (date: Date) =>
    date.getHours() === 23 &&
    date.getMinutes() === 59 &&
    date.getSeconds() === 59;
  return Boolean(
    (range?.from && !isMidnight(range.from)) ||
    (range?.to && !isLastSecond(range.to)),
  );
}

/** The vendor a filter value selects, or undefined for all vendors. */
function vendorFromSelectValue(value: string): string | undefined {
  return value.startsWith(VENDOR_VALUE_PREFIX)
    ? value.slice(VENDOR_VALUE_PREFIX.length)
    : undefined;
}

/** The file type a filter value selects, or undefined for all file types. */
function fileTypeFromSelectValue(value: string): string | undefined {
  return value.startsWith(FILE_TYPE_VALUE_PREFIX)
    ? value.slice(FILE_TYPE_VALUE_PREFIX.length)
    : undefined;
}

function VendorPortalFileRows({
  isLoading,
  files,
  selectedIds,
  onToggleSelected,
  onOnlyRun,
  selectable,
  onDownload,
}: {
  isLoading: boolean;
  files: VendorPortalFile[];
  selectedIds: ReadonlySet<string>;
  onToggleSelected: (documentId: string, checked: boolean) => void;
  onOnlyRun: (workflowRunId: string) => void;
  selectable: boolean;
  onDownload: (file: VendorPortalFile) => void;
}) {
  const gt = useGT();
  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={COLUMN_COUNT} className="h-24 text-center">
          <T context="Vendor portals table loading state">Loading...</T>
        </TableCell>
      </TableRow>
    );
  }
  if (files.length === 0) {
    return (
      <TableRow>
        <TableCell
          colSpan={COLUMN_COUNT}
          className="h-24 text-center text-muted-foreground"
        >
          <T context="Vendor portals table empty state">
            No files yet. Files appear here when a workflow&apos;s Browser Use
            Agent downloads one; the vendor is the workflow&apos;s name.
          </T>
        </TableCell>
      </TableRow>
    );
  }
  return (
    <>
      {files.map((file) => (
        <TableRow
          key={file.documentId}
          data-state={selectedIds.has(file.documentId) ? 'selected' : undefined}
        >
          <TableCell>
            <Checkbox
              checked={selectedIds.has(file.documentId)}
              disabled={!selectable}
              onCheckedChange={(checked) =>
                onToggleSelected(file.documentId, checked === true)
              }
              aria-label={gt('Select {fileName}', {
                context: 'Vendor portals checkbox that selects one file',
                fileName: file.fileName,
              })}
            />
          </TableCell>
          <TableCell className="font-mono text-xs">{file.fileName}</TableCell>
          <TableCell>{file.vendor}</TableCell>
          <TableCell>
            <VendorAccountLabel account={file.account} />
          </TableCell>
          <TableCell>
            {format(new Date(file.downloadedAt), ROW_DATETIME_FORMAT)}
          </TableCell>
          <TableCell>
            {file.fileSizeBytes === null
              ? '-'
              : formatFileSize(file.fileSizeBytes)}
          </TableCell>
          <TableCell>
            <VendorPortalFileSource file={file} onOnlyRun={onOnlyRun} />
          </TableCell>
          <TableCell className="text-right">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDownload(file)}
            >
              <DownloadSimpleIcon className="h-4 w-4" />
              <T context="Vendor portals table button that downloads a file">
                Download
              </T>
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}
